import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The page title.
 *
 * There is one heading per screen and it is drawn once, by the topbar — pages
 * no longer carry a heading of their own. This is the channel between the two:
 * a page declares its title, the shell renders it.
 *
 *   const NewsPage = () => (
 *     <S.Container>
 *       <PageTitle title="News" />
 *       ...
 *
 * The title is a string rather than a node on purpose. A node is a fresh
 * object on every render, which would make the effect below re-fire forever.
 */

interface IPageTitle {
  title: string;
  setTitle: (title: string) => void;
}

const PageTitleContext = createContext<IPageTitle>({ title: '', setTitle: () => {} });

export const PageTitleProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [title, setTitle] = useState('');
  const value = useMemo(() => ({ title, setTitle }), [title]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
};

/** Reads the current page title. The topbar is the only caller. */
export const usePageTitle = () => useContext(PageTitleContext).title;

const PageTitle: FC<{ title: string }> = ({ title }) => {
  const { setTitle } = useContext(PageTitleContext);

  useEffect(() => {
    setTitle(title);
    // Cleared on the way out so a screen that declares no title cannot inherit
    // the previous one. Unmount and mount land in the same commit, so nothing
    // flashes between two pages.
    return () => setTitle('');
  }, [title, setTitle]);

  return null;
};

export default PageTitle;
